import SectionLabel from "@/components/SectionLabel";
import { Layers, ChevronRight } from "lucide-react";

const OverlayVisual = () => {
  return (
    <section
      data-testid="overlay-visual-section"
      className="light-section py-24 lg:py-32 border-t border-[#e4e4e7] relative overflow-hidden"
    >
      <div className="aurora-soft" />
      <div className="relative max-w-[1380px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <SectionLabel index="—" label="Intelligence overlay, visualised" />
            <h2 className="mt-6 font-display font-bold text-[#090a0f] text-4xl sm:text-5xl lg:text-[52px] tracking-tight leading-[1.04]">
              Built to <span className="accent-violet">sit above</span>{" "}
              what you already run.
            </h2>
            <p className="mt-6 text-[#52525b] text-[17px] leading-relaxed max-w-[44ch]">
              Sentinel doesn&rsquo;t replace your KYC, AML, TM or fraud
              vendors. It reads what they output, resolves identity across
              them, and forms an independent opinion you can defend.
            </p>

            <div className="mt-8 space-y-2.5">
              {[
                { c: "bg-accent-violet", t: "Sentinel overlay" },
                { c: "bg-accent-cyan", t: "Your KYC / AML / TM / Fraud stack" },
                { c: "bg-accent-gold", t: "Your data sources" },
              ].map((r) => (
                <div
                  key={r.t}
                  className="flex items-center gap-3 text-[14px] text-[#52525b]"
                >
                  <span className={`inline-block w-2.5 h-2.5 ${r.c}`} />
                  <span>{r.t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative w-full aspect-[5/4] overflow-hidden border border-[#e4e4e7] bg-[#0d0e16]">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80"
                alt="Compliance intelligence overlay — data visualisation"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-50"
              />
              {/* Layered cards overlay */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 lg:p-10">
                <div className="relative w-full max-w-[480px] ml-auto">
                  {/* Layer 0 — sources */}
                  <div className="ml-12 mt-0 bg-white/95 border border-white/40 px-5 py-3 backdrop-blur-md shadow-xl">
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#f59e0b]">
                      Layer 00 — Sources
                    </p>
                    <p className="text-[#090a0f] text-[13px] mt-0.5">
                      KYC · AML · TM · Fraud · Core
                    </p>
                  </div>
                  {/* Layer 1 — vendors */}
                  <div className="ml-6 -mt-2 bg-white/95 border border-white/40 px-5 py-3 backdrop-blur-md shadow-xl">
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#00b8c9]">
                      Layer 01 — Vendor outputs
                    </p>
                    <p className="text-[#090a0f] text-[13px] mt-0.5">
                      Scores · Alerts · Verdicts · Logs
                    </p>
                  </div>
                  {/* Layer 2 — Sentinel */}
                  <div className="-mt-2 bg-gradient-to-br from-[#7c3aed] to-[#3b1f9c] border border-white/40 px-5 py-4 backdrop-blur-md shadow-2xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/70">
                          Layer 02 — Sentinel
                        </p>
                        <p className="font-display font-bold text-white text-[17px] tracking-tight mt-0.5">
                          Compliance Intelligence Overlay
                        </p>
                      </div>
                      <Layers className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                  </div>
                  {/* Output */}
                  <div className="mt-3 ml-auto w-fit bg-[#0d0e16] border border-[#00e5ff]/40 px-4 py-2 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] animate-pulse" />
                    <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#00e5ff]">
                      Independent verdict
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-[#00e5ff]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OverlayVisual;
